import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import PaymentValidationModal from './components/PaymentValidationModal';
import ServiceChargeModal from './components/ServiceChargeModal';
import ProfileUpgradeModal from './components/ProfileUpgradeModal';
import ProcessingModal from './components/ProcessingModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import APIRetryModal from './components/APIRetryModal';
import ServiceHeader from './components/ServiceHeader';
import ServiceDashboardHeader from './components/ServiceDashboardHeader';
import TransactionHistoryModal from './components/TransactionHistoryModal';
import ReadAloudButton from './components/ReadAloudButton';
import SegmentedReadAloud from './components/SegmentedReadAloud';
import PaymentFlow from './services/paymentFlow';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { toast } from "sonner";
import { Loader2, BookOpen, ChevronLeft } from "lucide-react";
import { convertToUserTimezone } from './utils/timezoneHandler';
import { useTimezone } from './contexts/TimezoneContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PDEPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { formatTimestamp } = useTimezone();
  
  // Helper function to clean AI-generated content (remove hashtags and asterisks)
  const cleanText = (text) => {
    if (!text) return '';
    return text.replace(/[#*]/g, '');
  };
  
  // formatTimestamp is already available from useTimezone() hook (line 34)
  
  // Check where user came from
  const fromLocation = searchParams.get('from');
  const returnPath = fromLocation === 'activity' ? '/user_dashboard' : '/homepage';
  const returnLabel = fromLocation === 'activity' ? 'Return to Dashboard' : 'Return to Home';
  
  // Amazon KDP Genre categories for manuscript analysis
  const genres = [
    { 
      value: "fiction", 
      label: "Fiction", 
      subGenres: [
        "Action & Adventure", "African American Fiction", "Anthologies", "Classics", 
        "Coming of Age", "Comics & Graphic Novels", "Contemporary Fiction", "Crime Fiction", 
        "Drama & Plays", "Erotica", "Fairy Tales, Folk Tales & Myths", "Fantasy", 
        "Historical Fiction", "Horror", "Humor & Satire", "Literary Fiction", 
        "Mystery, Thriller & Suspense", "Romance", "Science Fiction", "Short Stories", 
        "Teen & Young Adult Fiction", "Westerns", "Women's Fiction"
      ] 
    },
    { 
      value: "non-fiction", 
      label: "Non-Fiction", 
      subGenres: [
        "Arts & Photography", "Biographies & Memoirs", "Business & Money", 
        "Children's Nonfiction", "Comics & Graphic Novels (Nonfiction)", 
        "Computers & Technology", "Cookbooks, Food & Wine", "Crafts, Hobbies & Home",
        "Education & Teaching", "Engineering & Transportation", "Health, Fitness & Dieting",
        "History", "Humor & Entertainment", "Law", "LGBTQ+ Books", 
        "Literature & Fiction (Guides)", "Medical Books", "Parenting & Relationships",
        "Politics & Social Sciences", "Reference", "Religion & Spirituality",
        "Science & Math", "Self-Help", "Sports & Outdoors", 
        "Teen & Young Adult Nonfiction", "Travel"
      ] 
    },
    { 
      value: "poetry", 
      label: "Poetry", 
      subGenres: ["Contemporary Poetry", "Classic Poetry", "Spoken Word", "Haiku", "Free Verse"] 
    },
    { 
      value: "children", 
      label: "Children's Books", 
      subGenres: ["Picture Books", "Early Readers", "Middle Grade", "Young Adult", "Educational"] 
    },
    { 
      value: "academic", 
      label: "Academic/Educational", 
      subGenres: ["Textbook", "Research", "Technical Manual", "Reference", "Educational Guide", "Professional Development", "Training Materials"] 
    }
  ];

  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    author_name: "",
    publisher: "",
    genre: "",
    sub_genre: ""
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileRef = useRef(null); // Persist file reference
  const formDataRef = useRef(null); // Persist form data reference
  
  // API Retry Modal State
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');
  const maxRetryAttempts = 3;
  
  // Manual retry handler
  const handleManualRetry = async () => {
    setRetryAttempt(prev => prev + 1);
    setIsRetrying(true);
    setShowRetryModal(false); // Close modal while retrying
    
    try {
      // Trigger form submission again (form data is preserved in state)
      // Create a synthetic event to pass to handleSubmit
      const syntheticEvent = { preventDefault: () => {} };
      await handleSubmit(syntheticEvent);
      // If successful, reset retry state
      setRetryAttempt(0);
      setRetryError('');
    } catch (error) {
      // If still failing, show modal again
      console.error('❌ Manual retry failed:', error);
      setShowRetryModal(true);
    } finally {
      setIsRetrying(false);
    }
  };
  
  // Cancel retry
  const handleCancelRetry = async () => {
    setShowRetryModal(false);
    setRetryAttempt(0);
    setRetryError('');
    setShowForm(false);  // Return to dashboard view
    
    // Refresh reports list to check if analysis completed
    toast.info('Refreshing reports list to check if analysis completed...');
    await loadUsageStats();
    
    // Check if a new report was added
    const response = await axios.get(`${API}/pde/reports`, { withCredentials: true });
    const latestReports = response.data?.reports || [];
    if (latestReports.length > reports.length) {
      toast.success('Your PDE analysis completed successfully! The report has been added to your list.');
      // Refresh dashboard stats after successful service completion
      window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
    } else {
      toast.info('No new report found. The analysis may not have completed. Check back in a few minutes.');
    }
  };
  const [pricingInfo, setPricingInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [userHasPaymentMethod, setUserHasPaymentMethod] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Analyzing manuscript', completed: false, current: false },
    { text: 'Proofreading & editing', completed: false, current: false },
    { text: 'Generating final report', completed: false, current: false }
  ]);
  const [showPaymentValidation, setShowPaymentValidation] = useState(false);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [chargeInfo, setChargeInfo] = useState(null);
  const [serviceAccessDenied, setServiceAccessDenied] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [showForm, setShowForm] = useState(true);  // Show form directly (like ABG)
  const [reports, setReports] = useState([]);
  const [usageStats, setUsageStats] = useState({ total: 0, totalSpent: 0, paidUses: 0 });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [lastCharge, setLastCharge] = useState(null);

  // Three-State Model: 'overview' | 'form' | 'results'
  const [pageState, setPageState] = useState('overview');
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  // Service Details for Modals
  const serviceInfo = {
    name: "Professional Developmental Editor (PDE)",
    description: "Professional developmental editing to enhance manuscript structure, flow, and quality",
    amount: 15.00,
    subscriberPrice: 7.50,
    isPremium: true
  };

  // Load reports and calculate usage statistics
  const loadUsageStats = async () => {
    try {
      const response = await axios.get(`${API}/pde/reports`, { withCredentials: true });
      // Backend returns {reports: []} not the array directly
      const pdeReports = response.data?.reports || response.data || [];
      
      // Ensure it's an array
      const reportsArray = Array.isArray(pdeReports) ? pdeReports : [];
      setReports(reportsArray);
      
      // Calculate usage statistics
      const total = reportsArray.length;
      const paidUses = reportsArray.filter(r => r.charged || r.payment_amount > 0).length;
      const totalSpent = reportsArray.reduce((sum, r) => sum + (r.payment_amount || r.charge_amount || 0), 0);
      
      setUsageStats({ total, totalSpent, paidUses });
    } catch (error) {
      console.error('Error loading PDE reports:', error);
      setReports([]);
      setUsageStats({ total: 0, totalSpent: 0, paidUses: 0 });
    }
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    loadUsageStats();
  }, []);

  useEffect(() => {
    loadUserReports();
    
    // Check if we need to load a specific report from URL parameter
    // Support both ?viewReport={id} (legacy) and ?report={id} (new from dashboard)
    const reportId = searchParams.get('report') || searchParams.get('viewReport');
    const cancelled = searchParams.get('cancelled');
    
    // Check if user is returning from successful payment and restore form data
    const storedFormData = sessionStorage.getItem('pde_form_data');
    const paymentJustCompleted = sessionStorage.getItem('payment_just_completed');
    const returningFromPayment = !!(storedFormData || paymentJustCompleted);
    
    if (reportId) {
      // Loading specific report - allow access
      loadAndDisplayReport(reportId);
      setPaymentCompleted(true); // Skip payment validation for viewing reports
    } else if (cancelled) {
      // Returning from cancelled payment
      toast.error("Payment was cancelled. Please try again to access PDE service.");
      window.history.replaceState({}, document.title, window.location.pathname);
      // Don't auto-validate payment again, let user try manually
    } else if (returningFromPayment) {
      // User is returning from successful payment - grant access
      
      // Restore form data if available
      if (storedFormData) {
        try {
          const savedData = JSON.parse(storedFormData);
          setFormData(prevData => ({ ...prevData, ...savedData }));
        } catch (error) {
          console.error('Error restoring form data:', error);
        }
      }
      
      // Clean up stored data
      sessionStorage.removeItem('pde_form_data');
      sessionStorage.removeItem('payment_just_completed');
      
      toast.success("Payment successful! You now have access to PDE service.");
      setPaymentCompleted(true);
      
    } else {
      // NORMAL SERVICE ACCESS - VALIDATE PAYMENT FIRST
      validateServiceAccess();
    }

    // Listen for payment success messages from popup window
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'PAYMENT_SUCCESS') {
        toast.success("Payment successful! You can now start the analysis.");
        
        // Set payment completed and stop processing
        setPaymentCompleted(true);
        setPaymentProcessing(false);
        
        // Verify payment status but don't auto-start analysis
        verifyPaymentStatus(event.data.sessionId);
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [location]);

  const verifyPaymentStatus = async (sessionId) => {
    
    try {
      const statusResponse = await axios.get(
        `${API}/payments/checkout/status/${sessionId}`, 
        { withCredentials: true }
      );
      
      
      if (statusResponse.data.payment_status === 'paid') {
        setPaymentCompleted(true);
        setPaymentProcessing(false);
        
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        
      } else {
        setPaymentProcessing(false);
      }
      
    } catch (error) {
      console.error('🔥 Payment verification error:', error);
      setPaymentProcessing(false);
    }
  };

  const validateServiceAccess = async () => {
    try {
      
      const verification = await PaymentFlow.verifyPaymentForService('pde');
      
      if (verification.access_granted) {
        // Store charge info based on user type
        if (verification.discount_info) {
          // Subscriber - use discount info from backend
          setChargeInfo({
            name: serviceInfo.name,
            description: serviceInfo.description,
            amount: verification.discount_info.final_price,
            original_price: verification.discount_info.original_price,
            discount_percent: verification.discount_info.discount_percent,
            service_type: verification.discount_info.service_type
          });
        } else if (verification.charge_amount) {
          // A la carte - use charge_amount from backend
          setChargeInfo({
            name: serviceInfo.name,
            description: serviceInfo.description,
            amount: verification.charge_amount
          });
        } else {
          // Fallback to frontend pricing
          setChargeInfo({
            name: serviceInfo.name,
            description: serviceInfo.description,
            amount: serviceInfo.amount
          });
        }
        
        if (verification.requires_charge_authorization) {
          // A la Carte user - service access granted but needs charge authorization later
          setPaymentCompleted(true);
        } else if (verification.requires_charge_confirmation) {
          // Subscriber - needs confirmation modal showing discount before charging
          setPaymentCompleted(true);
        } else {
          // Free access (admin/student)
          setPaymentCompleted(true);
        }
        return;
      }
      
      if (verification.requires_payment_validation) {
        setShowPaymentValidation(true);
        setServiceAccessDenied(true);
        return;
      }
      
      // Fallback - deny access
      setServiceAccessDenied(true);
      
    } catch (error) {
      console.error('🔥 Service validation error:', error);
      setServiceAccessDenied(true);
    }
  };

  const handlePaymentValidationSuccess = (result, redirectLocation) => {
    // Backend returns 'user_role' not 'new_role'
    const userRole = result.user_role || result.new_role || 'a la carte';
    toast.success(`Payment method added! You are now an ${userRole.replace('_', ' ')} user. Refreshing...`);
    
    // Close modal immediately
    setShowPaymentValidation(false);
    
    // Reload the page to refresh user data and show the form
    // The page will reload with updated payment method, then user can click "Create New PDE" again
    setTimeout(() => {
      window.location.href = window.location.pathname;
    }, 800);
  };

  const handleServiceChargeAuthorization = async () => {
    try {
      // Close modal and proceed with service (payment handled by backend)
      setShowChargeModal(false);
      
      // Proceed with actual PDE analysis
      await submitForAnalysis();
    } catch (error) {
      console.error('🔥 Service charge authorization error:', error);
      const message = error.response?.data?.detail || error.message || 'Charge authorization failed';
      toast.error(message);
    }
  };

  const handleProfileUpgrade = async (upgradeType) => {
    try {
      
      // Call profile upgrade endpoint
      const response = await axios.post(`${API}/profile/upgrade`, {
        upgrade_type: upgradeType,
        service_context: 'pde'
      }, { withCredentials: true });
      
      if (response.data.success) {
        toast.success(`Profile upgraded to ${upgradeType.replace('_', ' ')} successfully!`);
        
        // Close modal and grant access
        setShowUpgradeModal(false);
        setPaymentCompleted(true);
        
        // Show confirmation message
        toast.success(`Welcome to your new ${upgradeType.replace('_', ' ')} account! You now have access to PDE service.`);
      } else {
        throw new Error(response.data.message || 'Upgrade failed');
      }
    } catch (error) {
      console.error('🔥 Profile upgrade error:', error);
      const message = error.response?.data?.detail || error.message || 'Profile upgrade failed';
      toast.error(message);
    }
  };

  const checkUserPaymentStatus = async () => {
    try {
      // Get fresh user data to check payment method
      const userResponse = await axios.get(`${API}/debug/user-profile`, { withCredentials: true });
      const userData = userResponse.data;
      
      
      const hasPaymentMethod = userData.has_payment_method;
      const isPayingRole = ['admin', 'student'].includes(userData.role) || hasPaymentMethod;
      
      setUserHasPaymentMethod(hasPaymentMethod);
      setPaymentCompleted(isPayingRole);
      
      
      return { hasPaymentMethod, isPayingRole, role: userData.role };
      
    } catch (error) {
      console.error('Error checking user payment status:', error);
      return { hasPaymentMethod: false, isPayingRole: false, role: 'free_user' };
    }
  };

  const checkPricing = async (showModal = true) => {
    try {
      // First, always check the user's actual payment status
      const paymentStatus = await checkUserPaymentStatus();
      
      // If user already has payment method or is in a free role, don't show modal
      if (paymentStatus.hasPaymentMethod || ['admin', 'student'].includes(paymentStatus.role)) {
        setPaymentCompleted(true);
        return;
      }

      // Check if we should skip modal due to recent payment
      const skipModal = sessionStorage.getItem('payment_return_url') || 
                       sessionStorage.getItem('payment_just_completed') || 
                       paymentCompleted;
      
      const response = await axios.get(`${API}/pde/pricing-check`, { withCredentials: true });
      setPricingInfo(response.data);
      
      // Only show payment modal if explicitly requested and payment is required
      if (response.data.payment_required && showModal && !skipModal) {
        showPDEPaymentCaptureModal({
          message: response.data.message,
          amount: response.data.price,
          service: 'PDE',
          type: response.data.type || 'payment_required'
        });
      }
    } catch (error) {
      console.error("Pricing check error:", error);
    }
  };

  // loadUsageStats moved to line 180 (combined with report loading)

  const loadUserReports = async () => {
    try {
      const response = await axios.get(`${API}/pde/reports`, { withCredentials: true });
      setReports(response.data.reports);
    } catch (error) {
      console.error("Load reports error:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.pdf') && !file.name.toLowerCase().endsWith('.docx')) {
        toast.error("Please select a PDF or DOCX file");
        return;
      }
      
      // Validate file size (50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error("File size must be under 50MB");
        return;
      }
      
      setSelectedFile(file);
      fileRef.current = file; // Also store in ref
      toast.success(`File selected: ${file.name}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    
    // Step 1: Validate required fields
    if (!formData.title || !formData.author_name || !formData.genre || !selectedFile) {
      toast.error("Please fill in all required fields (Title, Author, Genre) and select a file");
      return;
    }
    
    
    // Step 2: Check payment verification status
    
    try {
      // Check if user needs payment method setup (freemium or a_la_carte without payment method)
      if (user.role === 'freemium' || (user.role === 'a_la_carte' && !user.payment_method)) {
        setShowPaymentModal(true); // This will show the Payment Validation Modal
        return;
      }
      
      // Step 3: Payment verification passed - show PDE price confirmation modal
      setShowConfirmationModal(true);
      
    } catch (error) {
      console.error('🔥 Error checking payment status:', error);
      toast.error("Unable to verify payment status. Please try again.");
    }
  };
  
  const handleConfirmAnalysis = async () => {
    // User confirmed they want to proceed with analysis
    setShowConfirmationModal(false);
    
    // Now submit for analysis
    await submitForAnalysis();
  };
  
  const handleCancelAnalysis = () => {
    // User cancelled - just close modal and return to form
    setShowConfirmationModal(false);
  };
  
  const handleDeleteReport = async (reportId, reportTitle) => {
    setReportToDelete({ id: reportId, title: reportTitle });
    setShowDeleteModal(true);
  };

  const confirmDeleteReport = async () => {
    try {
      const response = await axios.delete(`${API}/pde/reports/${reportToDelete.id}`, { withCredentials: true });
      
      if (response.status === 200) {
        toast.success(`Report "${reportToDelete.title}" deleted successfully`);
        
        // Close modal first
        setShowDeleteModal(false);
        setReportToDelete(null);
        
        // Clear result and go back to dashboard
        setAnalysisResult(null);
        setShowForm(false);
        
        // Reload usage stats and reports list
        loadUsageStats();
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("Failed to delete report");
      setShowDeleteModal(false);
      setReportToDelete(null);
    }
  };

  const handleSendReceipt = async () => {
    try {
      const response = await axios.post(`${API}/send-receipt-email`, {
        transaction_id: lastTransactionId,
        user_email: user.email
      }, { withCredentials: true });
      toast.success("Receipt email sent successfully!");
      setShowEmailReceiptModal(false);
    } catch (error) {
      console.error('❌ Receipt email error:', error.response?.data || error.message);
      toast.error(`Receipt email not sent: ${error.response?.data?.detail || error.message}`);
      // Still close the modal even if email sending fails
      setShowEmailReceiptModal(false);
    }
  };

  const handleCloseEmailReceiptModal = () => {
    setShowEmailReceiptModal(false);
    toast.success("Analysis completed successfully!");
  };


  const performAnalysis = async () => {
    
    // Check if payment is required and modal isn't already showing
    if (pricingInfo?.payment_required && !showPaymentModal) {
      setShowPaymentModal(true);
      return;
    }
    
    // Proceed with analysis
    await submitForAnalysis();
  };

  const submitForAnalysis = async () => {
    
    // Use file from state or ref backup
    const currentFile = selectedFile || fileRef.current;
    
    if (!currentFile) {
      console.error('🔥 No file selected for analysis');
      toast.error("Please select a file first");
      return;
    }
    
    // Ensure state is synchronized with ref
    if (!selectedFile && fileRef.current) {
      setSelectedFile(fileRef.current);
    }

    
    try {
      setLoading(true);
      setIsProcessing(true); // Show processing modal
      
      // Step 1: Upload
      setCurrentStep('Uploading manuscript for analysis...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: false, current: true },
        { text: 'Proofreading & editing', completed: false, current: false },
        { text: 'Generating final report', completed: false, current: false }
      ]);
      setProcessingProgress(10);
      
      const formDataToSend = new FormData();
      
      // Use form data from state or ref backup
      const currentFormData = formData.title ? formData : (formDataRef.current || formData);
      
      // Add required fields (always add, even if empty)
      formDataToSend.append('title', currentFormData.title || '');
      formDataToSend.append('author_name', currentFormData.author_name || '');
      formDataToSend.append('genre', currentFormData.genre || '');
      
      // Add optional fields only if they have values
      if (currentFormData.subtitle) formDataToSend.append('subtitle', currentFormData.subtitle);
      if (currentFormData.publisher) formDataToSend.append('publisher', currentFormData.publisher);
      if (currentFormData.sub_genre) formDataToSend.append('sub_genre', currentFormData.sub_genre);
      
      formDataToSend.append('file', currentFile);
      
      setProcessingProgress(25);
      
      // Step 2: Proofreading & editing
      setCurrentStep('Performing comprehensive proofreading and developmental editing...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Proofreading & editing', completed: false, current: true },
        { text: 'Generating final report', completed: false, current: false }
      ]);
      setProcessingProgress(40);
      
      const response = await axios.post(`${API}/pde/upload-and-analyze`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
        timeout: 600000  // 10 minutes (PDE generates 9 sections with Claude API - can take 3-8 minutes)
      });
      
      // Step 3: Generating report
      setCurrentStep('Generating comprehensive editing report...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Proofreading & editing', completed: true, current: false },
        { text: 'Generating final report', completed: false, current: true }
      ]);
      setProcessingProgress(85);
      
      
      
      // OPTION A: Force Full Data Reload After Generation
      // Instead of using response.data directly, fetch the complete report from database
      // This ensures 100% consistency with Dashboard (Activity) page display
      const reportId = response.data.id;
      
      // Store charge info before reload
      const wasCharged = response.data.charged;
      const transactionId = response.data.transaction_id;
      const chargeAmount = response.data.charge_amount || (user.role === 'subscriber' ? 5.00 : 10.00);
      
      setPaymentCompleted(false);
      setPaymentProcessing(false);
      
      // Reload complete report from database (guarantees all 8 sections)
      await loadAndDisplayReport(reportId);
      
      // Complete
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Proofreading & editing', completed: true, current: false },
        { text: 'Generating final report', completed: true, current: false }
      ]);
      setProcessingProgress(100);
      setCurrentStep('Complete!');
      
      // Check if there was a charge
      if (wasCharged && transactionId) {
        setLastTransactionId(transactionId);
        setLastCharge({
          amount: chargeAmount,
          serviceName: "Professional Developmental Editor (PDE)",
          bookTitle: formData.title || "PDE Analysis"
        });
        // Show email receipt modal after processing
        setTimeout(() => {
          setIsProcessing(false);
          setShowEmailReceiptModal(true);
        }, 500);
      } else {
        setIsProcessing(false);
        toast.success("Analysis Completed. Service Fee Charged to Your Account");
      }
      
      // Refresh reports list and usage stats
      loadUsageStats();  // Reload dashboard stats
      setShowForm(false);  // Return to dashboard view
      
      // Scroll to top of report section
      setTimeout(() => {
        const reportSection = document.getElementById('pde-results');
        if (reportSection) {
          reportSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      
    } catch (error) {
      setIsProcessing(false); // Hide processing modal on error
      console.error('🔥 Analysis error:', error);
      console.error('🔥 Error response:', error.response?.data);
      console.error('🔥 Error status:', error.response?.status);
      console.error('🔥 Error code:', error.code);
      
      // Check for network error (connection dropped)
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        toast.error('Network connection lost during processing. The analysis may still be running on the server. Please check your reports list in a moment.', {
          duration: 10000
        });
        setRetryError('Network connection was interrupted. The PDE analysis process may still be running on the server. Please wait 2-3 minutes, then refresh this page and check your reports list. If the report appears, the analysis completed successfully despite the connection error.');
        setRetryAttempt(maxRetryAttempts);
        setIsRetrying(false);
        setShowRetryModal(true);
        return;
      }
      
      // Check for timeout error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. PDE analysis takes 3-8 minutes. Please try again or contact support if this persists.', {
          duration: 8000
        });
        setRetryError('Analysis timed out - the process may still be running on the server. Please wait a moment and check your reports list, or try submitting again.');
        setRetryAttempt(maxRetryAttempts);
        setIsRetrying(false);
        setShowRetryModal(true);
        return;
      }
      
      // Check for API overload error (503 with overloaded_error or overloaded_after_X_attempts)
      const errorDetail = error.response?.data?.detail || error.message || '';
      const errorStatus = error.response?.status;
      
      
      const isOverloadError = (errorStatus === 503) || 
                             (errorDetail && (
                               errorDetail.includes('overloaded') || 
                               errorDetail.includes('Overloaded') ||
                               errorDetail === 'overloaded_error'
                             ));
      
      if (isOverloadError) {
        // Show retry modal - don't lose form data!
        setRetryError(errorDetail);
        // Set attemptNumber to maxAttempts to show "retries failed" state with buttons
        setRetryAttempt(maxRetryAttempts);
        setIsRetrying(false); // Not currently retrying, waiting for user input
        setShowRetryModal(true);
        return; // Don't show error toast, let user retry
      }
      
      if (error.response?.status === 402) {
        // Backend says payment is required - show payment capture modal
        const errorData = error.response.data;
        showPDEPaymentCaptureModal({
          message: errorData.message || 'Payment required for PDE analysis',
          amount: errorData.amount || 10.0,
          service: 'PDE',
          type: errorData.type || 'payment_required'
        });
      } else {
        // Analysis failed for other reasons
        const errorMessage = error.response?.data?.detail || error.message || "Analysis failed";
        toast.error(`Analysis Failed: ${errorMessage}`);
      }
      
    } finally {
      setLoading(false);
      setShowPaymentModal(false);
    }
  };

  const initiateDirectPayment = async (packageId) => {
    try {
      
      // Store the current page and form data in sessionStorage for return after payment
      sessionStorage.setItem('payment_return_url', window.location.pathname);
      sessionStorage.setItem('payment_service', packageId);
      
      // Store form data if user was in the middle of filling out PDE form
      const currentFormData = {
        title: formData.title,
        author_name: formData.author_name,
        subtitle: formData.subtitle,
        publisher: formData.publisher,
        genre: formData.genre,
        sub_genre: formData.sub_genre
      };
      sessionStorage.setItem('pde_form_data', JSON.stringify(currentFormData));
      
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          package_id: packageId,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('🔥 Payment initiation error:', error);
      toast.error(`Payment setup failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const showPDEPaymentCaptureModal = (errorData) => {
    
    // Remove any existing modals first
    const existingModal = document.querySelector('[data-modal="pde-payment"]');
    if (existingModal) {
      existingModal.remove();
    }
    
    const modal = document.createElement('div');
    modal.setAttribute('data-modal', 'pde-payment');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white p-8 rounded-lg max-w-md w-full mx-4">
        <h3 class="text-xl font-bold mb-4">Premium Service - Payment Required</h3>
        <p class="text-gray-600 mb-4">
          You are currently using the <strong>FREE</strong> version of the app. This service requires payment verification.
        </p>
        <div class="bg-blue-50 p-3 rounded mb-4">
          <p class="text-sm text-blue-800">
            <strong>Service:</strong> ${errorData.service || 'PDE'}<br>
            <strong>Amount:</strong> $${errorData.amount || '10.00'}
          </p>
        </div>
        
        <div class="mb-6">
          <p class="text-sm font-medium text-gray-700 mb-3">How would you like to continue?</p>
          
          <div class="space-y-3">
            <div class="border rounded-lg p-3 cursor-pointer hover:bg-gray-50" id="option-a-la-carte">
              <div class="flex items-center">
                <input type="radio" name="payment-option" value="a-la-carte" id="radio-a-la-carte" class="mr-3">
                <div>
                  <div class="font-medium">Pay as You Go (A la Carte)</div>
                  <div class="text-sm text-gray-600">Pay only for services you use</div>
                </div>
              </div>
            </div>
            
            <div class="border rounded-lg p-3 cursor-pointer hover:bg-gray-50" id="option-subscription">
              <div class="flex items-center">
                <input type="radio" name="payment-option" value="subscription" id="radio-subscription" class="mr-3">
                <div>
                  <div class="font-medium">Monthly/Annual Subscription</div>
                  <div class="text-sm text-gray-600">Save 50% on premium services + free access to all standard tools</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="flex space-x-4">
          <button id="continue-payment-pde" class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex-1 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
            Continue
          </button>
          <button id="cancel-payment-pde" class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 flex-1">
            Cancel
          </button>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    // Function to safely remove modal
    const safeRemoveModal = () => {
      try {
        if (modal && modal.parentNode) {
          modal.parentNode.removeChild(modal);
        }
        // Also remove any other existing modals to prevent conflicts
        const existingModals = document.querySelectorAll('[data-modal="pde-payment"]');
        existingModals.forEach(existingModal => {
          if (existingModal.parentNode) {
            existingModal.parentNode.removeChild(existingModal);
          }
        });
      } catch (error) {
        console.error('Error removing modal:', error);
      }
    };
    
    // Handle modal actions with improved event binding
    setTimeout(() => {
      
      const continueButton = document.getElementById('continue-payment-pde');
      const cancelButton = document.getElementById('cancel-payment-pde');
      const aLaCarteOption = document.getElementById('option-a-la-carte');
      const subscriptionOption = document.getElementById('option-subscription');
      const radioALaCarte = document.getElementById('radio-a-la-carte');
      const radioSubscription = document.getElementById('radio-subscription');
      
      
      // Handle option selection
      if (aLaCarteOption && radioALaCarte) {
        const aLaCarteHandler = (e) => {
          radioALaCarte.checked = true;
          radioSubscription.checked = false;
          continueButton.disabled = false;
          continueButton.classList.remove('disabled:opacity-50', 'disabled:cursor-not-allowed');
        };
        aLaCarteOption.addEventListener('click', aLaCarteHandler);
        radioALaCarte.addEventListener('change', aLaCarteHandler);
      }
      
      if (subscriptionOption && radioSubscription) {
        const subscriptionHandler = (e) => {
          radioSubscription.checked = true;
          radioALaCarte.checked = false;
          continueButton.disabled = false;
          continueButton.classList.remove('disabled:opacity-50', 'disabled:cursor-not-allowed');
        };
        subscriptionOption.addEventListener('click', subscriptionHandler);
        radioSubscription.addEventListener('change', subscriptionHandler);
      }
      
      if (continueButton) {
        const continueClickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (continueButton.disabled) return;
          
          const selectedOption = document.querySelector('input[name="payment-option"]:checked')?.value;
          
          if (selectedOption === 'a-la-carte') {
            // Initiate direct payment for PDE service
            safeRemoveModal();
            initiateDirectPayment('pde');
          } else if (selectedOption === 'subscription') {
            // Redirect to pricing page for subscription selection
            safeRemoveModal();
            navigate('/prices?action=subscribe');
          } else {
            console.error('No payment option selected');
          }
        };
        
        continueButton.addEventListener('click', continueClickHandler);
      } else {
        console.error('❌ Continue button not found in DOM');
      }
      
      if (cancelButton) {
        const cancelClickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          safeRemoveModal();
        };
        
        cancelButton.addEventListener('click', cancelClickHandler);
      } else {
        console.error('❌ Cancel button not found in DOM');
      }
      
      // Close on outside click
      const outsideClickHandler = (e) => {
        if (e.target === modal) {
          safeRemoveModal();
        }
      };
      
      modal.addEventListener('click', outsideClickHandler);
      
      // Add escape key handler
      const escapeHandler = (e) => {
        if (e.key === 'Escape') {
          safeRemoveModal();
          document.removeEventListener('keydown', escapeHandler);
        }
      };
      
      document.addEventListener('keydown', escapeHandler);
      
    }, 250); // Increased timeout for better reliability
  };

  const handlePaymentAndAnalysis = async () => {
    // Immediately hide the payment modal to prevent double-clicking
    setShowPaymentModal(false);
    setPaymentProcessing(true);
    
    try {
      
      // Create Stripe checkout session
      const checkoutResponse = await axios.post(`${API}/payments/checkout/session`, {
        service: "pde",
        package_id: "pde_analysis",
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      
      if (checkoutResponse.data.checkout_url) {
        
        // Open Stripe checkout in a new window/tab
        const stripeWindow = window.open(checkoutResponse.data.checkout_url, '_blank', 'width=800,height=600');
        
        if (stripeWindow) {
          toast.success("Stripe checkout opened in new window. Complete payment there and return here.");
          
          // Monitor for window close
          const checkClosed = setInterval(() => {
            if (stripeWindow.closed) {
              clearInterval(checkClosed);
              toast.info("Payment window closed. If payment was successful, the analysis button will be activated.");
            }
          }, 1000);
          
        } else {
          throw new Error("Popup blocked. Please allow popups and try again.");
        }
      } else {
        console.error('🔥 No checkout_url in response:', checkoutResponse.data);
        throw new Error("Failed to create payment session - no checkout URL");
      }
    } catch (error) {
      console.error('🔥 Payment error:', error);
      console.error('🔥 Error details:', error.response?.data);
      toast.error("Failed to process payment. Please try again.");
      setPaymentProcessing(false);
      setShowPaymentModal(true); // Show modal again if payment fails
    }
  };

  const handleNewManuscript = () => {
    // Reset all form data
    setFormData({
      title: '',
      subtitle: '',
      author_name: '',
      publisher: '',
      genre: '',
      sub_genre: ''
    });
    
    // Clear file state
    setSelectedFile(null);
    fileRef.current = null; // Also clear ref
    formDataRef.current = null; // Clear form data ref
    
    // Reset payment states
    setPaymentCompleted(false);
    setPaymentProcessing(false);
    
    // Clear any analysis results
    setAnalysisResult(null);
    setLoading(false);
    
    // Show form view for new manuscript
    setShowForm(true);
    
    // Clear file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
    
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    toast.success("Ready for new manuscript submission!");
  };

  // Payment validation check before creating new PDE
  const handleCreateNewPDE = async () => {
    console.log('🔍 PDE Access Check - validating payment method...');
    
    // Check if user has payment method or is subscriber
    const isSubscriber = user?.subscription_status === 'monthly' || user?.subscription_status === 'annual' || user?.subscription_status === 'active';
    const hasPaymentMethod = user?.payment_method || user?.stripe_customer_id;
    const isFreeUser = user?.role === 'free_user';
    
    console.log('📊 User Status:', {
      isSubscriber,
      hasPaymentMethod,
      isFreeUser,
      role: user?.role,
      subscription: user?.subscription_status
    });
    
    // Admins and beta testers get free access
    if (user?.role === 'admin' || user?.role === 'beta_tester') {
      console.log('✅ Admin/Beta Tester - access granted');
      setShowForm(true);
      return;
    }
    
    // Subscribers get access (with discount)
    if (isSubscriber) {
      console.log('✅ Subscriber - access granted with discount');
      setShowForm(true);
      return;
    }
    
    // Free users are blocked
    if (isFreeUser) {
      console.log('❌ Free user - payment method required');
      toast.error('Please add a payment method or subscribe to access PDE');
      setShowPaymentValidation(true);
      return;
    }
    
    // A la carte users need payment method
    if (!hasPaymentMethod) {
      console.log('❌ No payment method - validation required');
      toast.error('Please add a payment method to access PDE');
      setShowPaymentValidation(true);
      return;
    }
    
    // All checks passed
    console.log('✅ Payment validation passed - showing form');
    setShowForm(true);
  };

  const loadAndDisplayReport = async (reportId) => {
    try {
      // Set loading state and clear previous results
      setLoading(true);
      setAnalysisResult(null);
      
      const response = await axios.get(`${API}/pde/reports/${reportId}`, { withCredentials: true });
      
      // Set the new analysis result with all 9 AIPRM sections
      setAnalysisResult({
        id: response.data.id,  // CRITICAL: ID needed for TTS caching
        title: response.data.title,
        subtitle: response.data.subtitle,
        author_name: response.data.author_name,
        publisher: response.data.publisher,
        genre: response.data.genre,
        sub_genre: response.data.sub_genre,
        analysis_report: response.data.analysis_report,
        // 9 Comprehensive AIPRM sections
        technical_overview: response.data.technical_overview,
        initial_analysis: response.data.initial_analysis,
        content_structure_analysis: response.data.content_structure_analysis,
        character_development: response.data.character_development,
        narrative_coherence: response.data.narrative_coherence,
        technical_aspects: response.data.technical_aspects,
        actionable_recommendations: response.data.actionable_recommendations,
        enhanced_content_sample: response.data.enhanced_content_sample,
        final_summary: response.data.final_summary,
        word_count: response.data.word_count,
        page_count: response.data.page_count,
        created_at: response.data.created_at
      });
      
      toast.success("Report loaded successfully!");
      setLoading(false);
      
      // Scroll to results
      setTimeout(() => {
        const resultsElement = document.querySelector('[data-results-section]');
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      toast.error("Failed to load report");
      console.error("Load report error:", error);
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    if (!analysisResult) {
      toast.error("No analysis result to export");
      return;
    }
    
    try {
      toast.info("Generating PDF... Please wait");
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 170;
      const leftMargin = 20;
      let yPosition = 20;
      
      // Helper function for page breaks
      const checkNewPage = (spaceNeeded) => {
        if (yPosition + spaceNeeded > 280) {
          pdf.addPage();
          yPosition = 20;
        }
      };
      
      // Helper function to add multi-line text
      const addMultiLineText = (text, fontSize = 10, maxWidth = pageWidth) => {
        if (!text) return;
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach(line => {
          checkNewPage(7);
          pdf.text(line, leftMargin, yPosition);
          yPosition += 6;
        });
      };
      
      // HEADER
      const timestamp = new Date().toLocaleString('en-US', {
        month: '2-digit', day: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true
      });
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(timestamp, leftMargin, yPosition);
      pdf.text('Digital Ink AI - PDE Report', 190, yPosition, { align: 'right' });
      yPosition += 10;
      
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175);
      pdf.text('DIGITAL INK AI', 105, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Professional Developmental Editor', 105, yPosition, { align: 'center' });
      yPosition += 15;
      
      // BOOK INFO
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(analysisResult.title || '', 105, yPosition, { align: 'center' });
      yPosition += 8;
      
      if (analysisResult.subtitle) {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'italic');
        pdf.text(analysisResult.subtitle, 105, yPosition, { align: 'center' });
        yPosition += 7;
      }
      
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      pdf.text(`by ${analysisResult.author_name || ''}`, 105, yPosition, { align: 'center' });
      yPosition += 15;
      
      // MANUSCRIPT STATS
      checkNewPage(30);
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Manuscript Statistics', leftMargin, yPosition);
      yPosition += 7;
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Word Count: ${analysisResult.word_count ? analysisResult.word_count.toLocaleString() : 'N/A'}`, leftMargin + 5, yPosition);
      yPosition += 6;
      pdf.text(`Page Count: ${analysisResult.page_count || 'N/A'}`, leftMargin + 5, yPosition);
      yPosition += 6;
      if (analysisResult.genre) {
        pdf.text(`Genre: ${analysisResult.genre}`, leftMargin + 5, yPosition);
        yPosition += 6;
      }
      if (analysisResult.sub_genre) {
        pdf.text(`Sub-Genre: ${analysisResult.sub_genre}`, leftMargin + 5, yPosition);
        yPosition += 6;
      }
      yPosition += 5;
      
      pdf.setFont(undefined, 'normal');
      
      // SECTION 1: TECHNICAL OVERVIEW
      if (analysisResult.technical_overview) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Technical Overview', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(cleanText(analysisResult.technical_overview));
        yPosition += 5;
      }
      
      // SECTION 2: INITIAL ANALYSIS
      if (analysisResult.initial_analysis) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Initial Analysis', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(cleanText(analysisResult.initial_analysis));
        yPosition += 5;
      }
      
      // SECTION 3: CONTENT STRUCTURE ANALYSIS
      if (analysisResult.content_structure_analysis) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Content Structure Analysis', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(cleanText(analysisResult.content_structure_analysis));
        yPosition += 5;
      }
      
      // SECTION 4: CHARACTER DEVELOPMENT
      if (analysisResult.character_development) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Character Development', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(cleanText(analysisResult.character_development));
        yPosition += 5;
      }
      
      // SECTION 5: NARRATIVE COHERENCE AND FLOW
      if (analysisResult.narrative_coherence) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Narrative Coherence and Flow', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(cleanText(analysisResult.narrative_coherence));
        yPosition += 5;
      }
      
      // SECTION 6: TECHNICAL ASPECTS
      if (analysisResult.technical_aspects) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Technical Aspects', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(cleanText(analysisResult.technical_aspects));
        yPosition += 5;
      }
      
      // SECTION 7: ACTIONABLE RECOMMENDATIONS
      if (analysisResult.actionable_recommendations) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Actionable Recommendations', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(cleanText(analysisResult.actionable_recommendations));
        yPosition += 5;
      }
      
      // SECTION 8: ENHANCED CONTENT SAMPLE
      if (analysisResult.enhanced_content_sample) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Enhanced Content Sample', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(cleanText(analysisResult.enhanced_content_sample));
        yPosition += 5;
      }
      
      // SECTION 9: FINAL SUMMARY AND NEXT STEPS
      if (analysisResult.final_summary) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Final Summary and Next Steps', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(cleanText(analysisResult.final_summary));
        yPosition += 5;
      }
      
      // Footer on all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${totalPages}`, 105, 287, { align: 'center' });
        pdf.text('www.DigitalInkAI.com', 105, 292, { align: 'center' });
      }
      
      const filename = `PDE_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error('PDF error:', error);
      toast.error(`Failed to generate PDF: ${error.message}`);
    }
  };

  const printResults = () => {
    if (!analysisResult) return;
    
    const printWindow = window.open('', '_blank');
    const resultsElement = document.getElementById('pde-results');
    
    if (resultsElement && printWindow) {
      // Format timestamp for header
      const now = new Date();
      const timestamp = now.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      const generatedDate = now.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Digital Ink AI - PDE Report</title>
            <style>
              body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; margin: 15px; line-height: 1.5; }
              .print-header { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 10pt; color: #666; }
              .pde-header { text-align: center; margin-bottom: 20px; }
              .pde-header img { height: 80px; margin: 0 auto 10px; display: block; }
              .pde-header h1 { font-size: 16pt; margin: 8px 0; font-family: 'Times New Roman', Times, serif; color: #1e40af; font-style: italic; }
              .pde-header h2 { font-size: 18pt; margin: 5px 0; font-family: 'Times New Roman', Times, serif; font-weight: bold; }
              .pde-header p { font-size: 11pt; margin: 3px 0; color: #666; }
              .generated-date { text-align: center; font-size: 10pt; color: #666; margin-top: 15px; }
              .metrics-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 15px 0; }
              .metric-card { border: 1px solid #ddd; padding: 10px; text-align: center; border-radius: 6px; }
              .metric-score { font-size: 18pt; font-weight: bold; margin-bottom: 3px; }
              .metric-label { font-size: 10pt; color: #666; }
              .report-content { white-space: pre-wrap; font-family: 'Times New Roman', Times, serif; line-height: 1.5; }
              .section { margin-bottom: 10px; padding: 10px; border-left: 4px solid; border-radius: 6px; page-break-inside: avoid; }
              .section h3 { font-size: 14pt; font-weight: bold; margin: 0 0 6px 0; font-family: 'Times New Roman', Times, serif; }
              .section p { margin: 0; font-size: 12pt; line-height: 1.4; font-family: 'Times New Roman', Times, serif; }
              .manuscript-info { margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #ddd; }
              .manuscript-info h2 { font-size: 16pt; font-weight: bold; margin: 0 0 3px 0; font-family: 'Times New Roman', Times, serif; }
              .manuscript-info p { font-size: 12pt; margin: 3px 0; font-family: 'Times New Roman', Times, serif; }
              .footer { margin-top: 15px; text-align: center; font-size: 10pt; color: #666; border-top: 1px solid #ddd; padding-top: 12px; }
              @media print { body { margin: 10px; } .no-print { display: none; } }
            </style>
          </head>
          <body>
            <!-- Standard Header -->
            <div class="print-header">
              <div>${timestamp}</div>
              <div>Digital Ink AI - PDE Report</div>
            </div>
            
            <div class="pde-header">
              <img src="/Digital_Ink_AI_Logo.png" alt="Digital Ink AI" onload="window.print();" />
              <h1>Professional Developmental Editor</h1>
              <h2>${analysisResult.title}</h2>
              <p>by ${analysisResult.author_name}</p>
            </div>
            
            <div class="generated-date">Generated ${generatedDate}</div>
            
            <div class="metrics-grid">
              <div class="metric-card">
                <div class="metric-score" style="color: #9333ea;">${analysisResult.word_count ? analysisResult.word_count.toLocaleString() : 'N/A'}</div>
                <div class="metric-label">Words</div>
              </div>
              <div class="metric-card">
                <div class="metric-score" style="color: #ea580c;">${analysisResult.page_count || 'N/A'}</div>
                <div class="metric-label">Pages</div>
              </div>
            </div>
            
            <!-- Manuscript Title and Author -->
            <div class="manuscript-info">
              <h2>${analysisResult.title}</h2>
              ${analysisResult.subtitle ? `<p style="font-style: italic; color: #475569;">${analysisResult.subtitle}</p>` : ''}
              <p style="font-weight: 600;">by ${analysisResult.author_name}</p>
              ${analysisResult.publisher ? `<p style="font-size: 12px; color: #64748b; margin-top: 5px;">Publisher: ${analysisResult.publisher}</p>` : ''}
            </div>
            
            <!-- PDE Analysis Sections - All 9 AIPRM Sections -->
            <div class="report-content">
              
              ${analysisResult.technical_overview ? `
              <div class="section" style="background-color: #f8fafc; border-left-color: #64748b;">
                <h3 style="color: #1e293b;">📋 Technical Overview</h3>
                <p style="color: #334155;">${cleanText(analysisResult.technical_overview)}</p>
              </div>` : ''}
              
              ${analysisResult.initial_analysis ? `
              <div class="section" style="background-color: #dbeafe; border-left-color: #3b82f6;">
                <h3 style="color: #1e3a8a;">🔍 Initial Analysis</h3>
                <p style="color: #1e40af;">${cleanText(analysisResult.initial_analysis)}</p>
              </div>` : ''}
              
              ${analysisResult.content_structure_analysis ? `
              <div class="section" style="background-color: #f3e8ff; border-left-color: #9333ea;">
                <h3 style="color: #581c87;">🏗️ Content Structure Analysis</h3>
                <p style="color: #6b21a8;">${cleanText(analysisResult.content_structure_analysis)}</p>
              </div>` : ''}
              
              ${analysisResult.character_development ? `
              <div class="section" style="background-color: #ede9fe; border-left-color: #8b5cf6;">
                <h3 style="color: #5b21b6;">👥 Character Development</h3>
                <p style="color: #6d28d9;">${cleanText(analysisResult.character_development)}</p>
              </div>` : ''}
              
              ${analysisResult.narrative_coherence ? `
              <div class="section" style="background-color: #e0e7ff; border-left-color: #6366f1;">
                <h3 style="color: #3730a3;">🌊 Narrative Coherence and Flow</h3>
                <p style="color: #4338ca;">${cleanText(analysisResult.narrative_coherence)}</p>
              </div>` : ''}
              
              ${analysisResult.technical_aspects ? `
              <div class="section" style="background-color: #cffafe; border-left-color: #06b6d4;">
                <h3 style="color: #164e63;">⚙️ Technical Aspects</h3>
                <p style="color: #155e75;">${cleanText(analysisResult.technical_aspects)}</p>
              </div>` : ''}
              
              ${analysisResult.actionable_recommendations ? `
              <div class="section" style="background-color: #d1fae5; border-left-color: #10b981;">
                <h3 style="color: #065f46;">💡 Actionable Recommendations</h3>
                <p style="color: #047857;">${cleanText(analysisResult.actionable_recommendations)}</p>
              </div>` : ''}
              
              ${analysisResult.enhanced_content_sample ? `
              <div class="section" style="background-color: #fef3c7; border-left-color: #f59e0b;">
                <h3 style="color: #92400e;">✨ Enhanced Content Sample</h3>
                <p style="color: #78350f;">${cleanText(analysisResult.enhanced_content_sample)}</p>
              </div>` : ''}
              
              ${analysisResult.final_summary ? `
              <div class="section" style="background-color: #ffe4e6; border-left-color: #f43f5e;">
                <h3 style="color: #881337;">🚀 Final Summary and Next Steps</h3>
                <p style="color: #9f1239;">${cleanText(analysisResult.final_summary)}</p>
              </div>` : ''}
              
            </div>
            
            <div class="footer">
              <p>Generated with Digital Ink AI - www.DigitalInkAI.com</p>
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      // Print will be triggered by image onload
    }
  };

  // Use central MST formatter

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header with Logo and Navigation - Hidden when viewing results */}
      {!analysisResult && (
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src="/Digital_Ink_AI_Logo.png" 
                  alt="Digital Ink AI Logo" 
                  className="h-12 w-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                  }}
                />
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={() => navigate('/homepage')}>
                  🏠 Home
                </Button>
                <Button variant="outline" onClick={() => navigate('/user_dashboard')}>
                  📊 Dashboard
                </Button>
              </div>
            </div>
          </div>
        </header>
      )}

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Dashboard View - Shows statistics and reports (ABG pattern) */}
        {!showForm && !analysisResult && !loading && (
          <div className="space-y-8">
            {/* Service Dashboard Header - Consistent across all services */}
            <ServiceDashboardHeader 
              serviceCode="PDE"
              serviceName="Professional Developmental Editor"
              icon="✍️"
              gradientFrom="from-violet-500"
              gradientTo="to-fuchsia-600"
              totalReports={usageStats.total}
              totalSpent={usageStats.totalSpent}
              paidUses={usageStats.paidUses}
              onCreateNew={handleCreateNewPDE}
            />

            {/* Service Description */}
            <div className="text-center mb-8">
              <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
                Professional developmental editing to enhance manuscript structure, flow, and quality. Upload your manuscript to receive a comprehensive nine-section analysis covering technical overview, content structure, character development, narrative coherence, technical aspects, actionable recommendations, enhanced content samples, and next steps. Transform your manuscript with expert-level insights designed to elevate your writing to professional publishing standards.
              </p>
            </div>

            {/* Reports List Section */}
            {reports && reports.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl text-slate-900">
                    📚 Professional Developmental Editor Reports ({reports.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {reports.map((report) => (
                      <div
                        key={report.id}
                        className="flex items-center justify-between p-4 bg-violet-50 rounded-lg border border-violet-200 hover:shadow-md transition-shadow"
                      >
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{report.title}</h3>
                          {report.subtitle && (
                            <p className="text-sm text-slate-600">{report.subtitle}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            <span>📅 {formatTimestamp(report.created_at)}</span>
                            <span>👤 {report.author_name}</span>
                            <span>📖 {report.word_count?.toLocaleString()} words</span>
                            {report.payment_amount > 0 && (
                              <Badge variant="secondary" className="bg-violet-100 text-violet-700">
                                ${report.payment_amount} paid
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                            onClick={() => loadAndDisplayReport(report.id)}
                          >
                            📖 View Report
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteReport(report.id, report.title)}
                          >
                            🗑️
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty State */}
            {reports && reports.length === 0 && (
              <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
                <CardContent className="py-12 text-center">
                  <div className="text-6xl mb-4">✍️</div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">No Reports Yet</h3>
                  <p className="text-slate-600 mb-6">Get started with your first Professional Developmental Editor analysis</p>
                  <Button
                    size="lg"
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={handleCreateNewPDE}
                  >
                    ✍️ Create Your First PDE Report
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Form View - Shows when Create New PDE is clicked */}
        {showForm && !analysisResult && !loading && (
          <>
            {/* Service Title and Description */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                ✍️ Professional Developmental Editor
              </h1>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
                Enhance your manuscript's structure, flow, and quality with professional developmental editing. Upload your draft for comprehensive AI-driven analysis of structure, character development, pacing, thematic consistency, and voice preservation. Receive detailed feedback and actionable recommendations while preserving your unique authorial voice.
              </p>
            </div>

            {/* Pricing Info */}
            {pricingInfo && (
              <div className="mb-8 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-6">
                <div className="text-center">
                  {pricingInfo.payment_required ? (
                    <div>
                      <h3 className="text-xl font-bold text-violet-900 mb-2">💎 Standard Service - ${pricingInfo.price}</h3>
                      <p className="text-violet-700">Professional developmental editing charged to your payment method on file</p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                      <p className="text-green-700">Included with your subscription - get as many analyses as you need!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Analysis Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="text-center mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                    alt="Digital Ink AI" 
                    className="h-10 mx-auto mb-3"
                  />
                  <CardTitle className="text-2xl text-slate-900">📄 Manuscript Submission</CardTitle>
                  <p className="text-sm text-slate-600 mt-1">Powered by Digital Ink AI</p>
                </div>
              </CardHeader>
              <CardContent>
                {!paymentCompleted ? (
                  // Payment validation in progress - show loading or payment modal
                  <div className="text-center py-8">
                    <div className="animate-pulse">
                      <div className="w-12 h-12 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Validating Service Access</h3>
                    <p className="text-slate-600 mb-4">Please wait while we verify your payment information...</p>
                    <Button 
                      onClick={validateServiceAccess}
                      variant="outline"
                      className="mt-4"
                    >
                      Retry Validation
                    </Button>
                  </div>
                ) : (
                  // Payment completed - show full form
                  <div>
                    {/* New Manuscript Button */}
                    <div className="mb-6 flex justify-start">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleNewManuscript}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  >
                    📝 New Manuscript
                  </Button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Manuscript Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                        placeholder="Enter your book title"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="subtitle">Subtitle</Label>
                      <Input
                        id="subtitle"
                        name="subtitle"
                        value={formData.subtitle}
                        onChange={handleInputChange}
                        className="mt-1"
                        placeholder="Enter subtitle (optional)"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="author_name">Author Name *</Label>
                      <Input
                        id="author_name"
                        name="author_name"
                        value={formData.author_name}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                        placeholder="Author's full name"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="publisher">Publisher</Label>
                      <Input
                        id="publisher"
                        name="publisher"
                        value={formData.publisher}
                        onChange={handleInputChange}
                        className="mt-1"
                        placeholder="Publisher (if applicable)"
                      />
                    </div>
                  </div>

                  {/* Genre Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="genre">Genre *</Label>
                      <select
                        id="genre"
                        name="genre"
                        value={formData.genre}
                        onChange={handleInputChange}
                        required
                        className="mt-1 w-full p-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select primary genre</option>
                        {genres.map((genre) => (
                          <option key={genre.value} value={genre.value}>
                            {genre.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <Label htmlFor="sub_genre">Sub-Genre</Label>
                      <select
                        id="sub_genre"
                        name="sub_genre"
                        value={formData.sub_genre}
                        onChange={handleInputChange}
                        className="mt-1 w-full p-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        disabled={!formData.genre}
                      >
                        <option value="">Select sub-genre (optional)</option>
                        {formData.genre && genres.find(g => g.value === formData.genre)?.subGenres.map((subGenre) => (
                          <option key={subGenre} value={subGenre}>
                            {subGenre}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* File Upload */}
                  <div>
                    <Label htmlFor="file">Manuscript File *</Label>
                    <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        id="file"
                        accept=".pdf,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <label htmlFor="file" className="cursor-pointer">
                        <div className="text-4xl mb-4">📁</div>
                        {selectedFile ? (
                          <div>
                            <p className="text-violet-600 font-medium">{selectedFile.name}</p>
                            <p className="text-sm text-slate-500">
                              {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-slate-600 font-medium">Click to select file</p>
                            <p className="text-sm text-slate-500">PDF or DOCX files only (max 50MB)</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading || paymentProcessing || !paymentCompleted}
                  >
                    {loading ? 'Analyzing Manuscript...' : paymentProcessing ? 'Processing Payment...' : 'Analyze Manuscript with Digital Ink AI'}
                  </button>
                </form>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Analysis Features */}
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Analysis Features</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <span className="text-violet-500 mr-2">✓</span>
                  <span>Comprehensive structural analysis</span>
                </div>
                <div className="flex items-start">
                  <span className="text-violet-500 mr-2">✓</span>
                  <span>Character arc and development assessment</span>
                </div>
                <div className="flex items-start">
                  <span className="text-violet-500 mr-2">✓</span>
                  <span>Narrative coherence and flow evaluation</span>
                </div>
                <div className="flex items-start">
                  <span className="text-violet-500 mr-2">✓</span>
                  <span>Pacing and tone analysis</span>
                </div>
                <div className="flex items-start">
                  <span className="text-violet-500 mr-2">✓</span>
                  <span>Technical writing assessment</span>
                </div>
                <div className="flex items-start">
                  <span className="text-violet-500 mr-2">✓</span>
                  <span>Enhanced content sample with revision notes</span>
                </div>
                <div className="flex items-start">
                  <span className="text-violet-500 mr-2">✓</span>
                  <span>Prioritized improvement roadmap</span>
                </div>
              </div>
            </div>

            {/* Recent Reports */}
            {reports.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg text-slate-900">📋 Recent Reports</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {reports.slice(0, 5).map((report, index) => (
                      <div key={index} className="p-3 bg-slate-50 rounded border">
                        <div className="font-medium text-sm truncate">{report.title}</div>
                        <div className="text-xs text-slate-500 mb-2">
                          {report.created_at_display || 'Recently'}
                        </div>
                        {/* PDE does not use scoring */}
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => loadAndDisplayReport(report.id)}
                          >
                            📖 View Report
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteReport(report.id, report.title)}
                          >
                            🗑️
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-3"
                    onClick={() => navigate('/user_dashboard')}
                  >
                    View All in Dashboard
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
        </>
        )}

        {/* Loading indicator when fetching report */}
        {loading && !analysisResult && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
              <p className="text-lg text-slate-600">Loading your PDE report...</p>
            </div>
          </div>
        )}

        {/* Analysis Results - BCTG-style Professional Layout */}
        {analysisResult && (
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100">
            {/* Header - ABG Style */}
            <header className="bg-white shadow-sm border-b print:hidden">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img 
                      src="/Digital_Ink_AI_Logo.png" 
                      alt="Digital Ink AI Logo" 
                      className="h-12 w-auto"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/homepage')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      🏠 Home
                    </button>
                    <button
                      onClick={() => navigate('/user_dashboard')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      📊 Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </header>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-6 py-8">
              <div className="bg-white rounded-lg shadow-lg p-8">
                {/* Print Header - Hidden on Screen */}
                <div className="hidden print:block mb-6 pb-4 border-b-2 border-gray-300">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                        alt="Digital Ink AI" 
                        className="h-12 w-auto"
                      />
                      <div>
                        <h1 className="text-2xl font-bold text-gray-800">Digital Ink AI</h1>
                        <p className="text-sm text-gray-600">Professional Developmental Editor</p>
                        <p className="text-xs text-gray-600 mt-1">Book: {analysisResult.title} | Author: {analysisResult.author_name}</p>
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-600">
                      <p>{new Date().toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Report Metadata - ABG Style */}
                <div className="border-b pb-6 mb-6 print:border-b-2">
                  <div className="flex justify-between items-start mb-4 print:mb-2">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900 print:text-3xl">PDE Analysis Report</h2>
                      <p className="text-slate-600 mt-1">Manuscript Analysis for {analysisResult.title}</p>
                      <p className="text-slate-600 text-sm">by {analysisResult.author_name}</p>
                    </div>
                    <div className="flex gap-3 print:hidden">
                      <button onClick={printResults} className="hidden md:inline-flex px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">🖨️ Print</button>
                      <button onClick={exportToPDF} className="hidden md:inline-flex px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">📄 Export PDF</button>
                      <button onClick={handleNewManuscript} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">✨ New Analysis</button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Book Title:</span>
                      <p className="font-semibold text-slate-900">{analysisResult.title || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Author:</span>
                      <p className="font-semibold text-slate-900">{analysisResult.author_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Service:</span>
                      <p className="font-semibold text-slate-900">Professional Developmental Editor</p>
                    </div>
                  </div>
                </div>

              <div id="pde-results" className="space-y-8">
                {/* Analysis Details Card - Enhanced Visibility */}
                <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-lg">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-2xl font-bold text-slate-900">📋 Analysis Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Prominent Title and Author */}
                      <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">{analysisResult.title}</h2>
                        {analysisResult.subtitle && (
                          <p className="text-lg text-slate-700 italic mb-2">{analysisResult.subtitle}</p>
                        )}
                        <p className="text-xl text-slate-800 mb-3">by {analysisResult.author_name}</p>
                        
                        {/* Publisher */}
                        {analysisResult.publisher && (
                          <div className="mt-4 pt-3 border-t border-slate-200">
                            <span className="text-sm text-slate-600 font-semibold">Publisher: </span>
                            <span className="text-base text-slate-800">{analysisResult.publisher}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Book Statistics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                          <div className="text-2xl font-bold text-blue-600">{analysisResult.word_count.toLocaleString()}</div>
                          <div className="text-sm text-slate-600 mt-1">Word Count</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                          <div className="text-2xl font-bold text-violet-600">{analysisResult.page_count || Math.round(analysisResult.word_count / 250)}</div>
                          <div className="text-sm text-slate-600 mt-1">Est. Pages</div>
                        </div>
                        {analysisResult.genre && (
                          <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                            <div className="text-lg font-bold text-purple-600">{analysisResult.genre.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                            <div className="text-sm text-slate-600 mt-1">Genre</div>
                          </div>
                        )}
                        {analysisResult.sub_genre && (
                          <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                            <div className="text-lg font-bold text-indigo-600">{analysisResult.sub_genre}</div>
                            <div className="text-sm text-slate-600 mt-1">Sub-Genre</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* PDE does not use scoring - removed Key Metrics Card */}

                {/* Analysis Report Card - 6 Structured Sections */}
                <Card className="bg-white border-slate-200">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-indigo-50">
                    <CardTitle className="text-lg text-slate-900">📊 Comprehensive Analysis Report</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 p-6">
                    {/* Segmented Read Aloud - ABOVE all sections */}
                    <div className="mb-6 print:hidden">
                      <SegmentedReadAloud 
                        text={[
                          analysisResult.technical_overview,
                          analysisResult.initial_analysis,
                          analysisResult.content_structure_analysis,
                          analysisResult.character_development,
                          analysisResult.narrative_coherence,
                          analysisResult.technical_aspects,
                          analysisResult.actionable_recommendations,
                          analysisResult.enhanced_content_sample,
                          analysisResult.final_summary
                        ].filter(Boolean).join('\n\n')}
                        speechId={analysisResult.id}
                        serviceType="pde"
                      />
                    </div>

                    {/* Section 1: Technical Overview */}
                    <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
                      <h4 className="font-bold text-slate-900 text-lg mb-2">📋 Technical Overview</h4>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {analysisResult.technical_overview ? analysisResult.technical_overview.replace(/[#*]/g, '') : "No data available"}
                      </p>
                    </div>

                    {/* Section 2: Initial Analysis */}
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                      <h4 className="font-bold text-slate-900 text-lg mb-2">🔍 Initial Analysis</h4>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {analysisResult.initial_analysis ? analysisResult.initial_analysis.replace(/[#*]/g, '') : "No data available"}
                      </p>
                    </div>

                    {/* Section 3: Content Structure Analysis */}
                    <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                      <h4 className="font-bold text-slate-900 text-lg mb-2">🏗️ Content Structure Analysis</h4>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {analysisResult.content_structure_analysis ? analysisResult.content_structure_analysis.replace(/[#*]/g, '') : "No data available"}
                      </p>
                    </div>

                    {/* Section 4: Character Development */}
                    <div className="bg-violet-50 p-4 rounded-lg border-l-4 border-violet-400">
                      <h4 className="font-bold text-slate-900 text-lg mb-2">👥 Character Development</h4>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {analysisResult.character_development ? analysisResult.character_development.replace(/[#*]/g, '') : "No data available"}
                      </p>
                    </div>

                    {/* Section 5: Narrative Coherence and Flow */}
                    <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400">
                      <h4 className="font-bold text-slate-900 text-lg mb-2">🌊 Narrative Coherence and Flow</h4>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {analysisResult.narrative_coherence ? analysisResult.narrative_coherence.replace(/[#*]/g, '') : "No data available"}
                      </p>
                    </div>

                    {/* Section 6: Technical Aspects */}
                    <div className="bg-cyan-50 p-4 rounded-lg border-l-4 border-cyan-400">
                      <h4 className="font-bold text-slate-900 text-lg mb-2">⚙️ Technical Aspects</h4>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {analysisResult.technical_aspects ? analysisResult.technical_aspects.replace(/[#*]/g, '') : "No data available"}
                      </p>
                    </div>

                    {/* Section 7: Actionable Recommendations */}
                    <div className="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400">
                      <h4 className="font-bold text-slate-900 text-lg mb-2">💡 Actionable Recommendations</h4>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {analysisResult.actionable_recommendations ? analysisResult.actionable_recommendations.replace(/[#*]/g, '') : "No data available"}
                      </p>
                    </div>

                    {/* Section 8: Enhanced Content Sample */}
                    <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-400">
                      <h4 className="font-bold text-slate-900 text-lg mb-2">✨ Enhanced Content Sample</h4>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {analysisResult.enhanced_content_sample ? analysisResult.enhanced_content_sample.replace(/[#*]/g, '') : "No data available"}
                      </p>
                    </div>

                    {/* Section 9: Final Summary and Next Steps */}
                    <div className="bg-rose-50 p-4 rounded-lg border-l-4 border-rose-400">
                      <h4 className="font-bold text-slate-900 text-lg mb-2">🚀 Final Summary and Next Steps</h4>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {analysisResult.final_summary ? analysisResult.final_summary.replace(/[#*]/g, '') : "No data available"}
                      </p>
                    </div>

                    {/* Next Steps */}
                    <div className="border-t border-slate-200 pt-6">
                      <h4 className="font-bold text-slate-900 mb-3 text-lg">🚀 Recommended Next Tools</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="p-3 bg-slate-50 rounded-md text-sm">
                          <div className="font-semibold text-slate-900">BOCP</div>
                          <div className="text-slate-600">Book Outline & Chapter Planner - $3.00</div>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-md text-sm">
                          <div className="font-semibold text-slate-900">BBSG</div>
                          <div className="text-slate-600">Book Blurb & Synopsis Generator - $2.50</div>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-md text-sm">
                          <div className="font-semibold text-slate-900">MCB</div>
                          <div className="text-slate-600">Marketing Campaign Builder - $4.00</div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mt-3 italic">
                        💡 Upgrade to a subscription for unlimited access and significant savings!
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Footer */}
                <div className="text-center py-8 border-t border-slate-200">
                  <p className="text-sm text-slate-500">
                    Generated by Digital Ink AI - AI-Powered Publishing Tools
                  </p>
                </div>
              </div>
              </div>
            </main>
          </div>
        )}
      </main>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium mb-4">💳 Payment Required</h3>
              <p className="text-slate-600 mb-4">
                PDE analysis costs ${pricingInfo?.price} for detailed manuscript evaluation.
              </p>
              
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-blue-900 mb-2">What You'll Receive:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Comprehensive 4-phase analysis</li>
                  <li>• Quality scores and market assessment</li>
                  <li>• Development recommendations</li>
                  <li>• Commercial potential evaluation</li>
                  <li>• Professional PDF report</li>
                </ul>
              </div>
              
              <div className="flex space-x-3">
                <Button 
                  onClick={handlePaymentAndAnalysis}
                  className="flex-1 bg-violet-600 hover:bg-violet-700"
                >
                  Pay ${pricingInfo?.price} & Analyze
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
              
              <p className="text-xs text-slate-500 text-center mt-3">
                Secure payment processed via Stripe
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Paid Analysis */}
      {showConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="text-center">
              <div className="text-4xl mb-4">💎</div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                PDE Analysis - Confirm & Proceed
              </h3>
              
              {user.subscription_status && user.subscription_status !== "None" ? (
                // Has active subscription - 50% discount
                <div className="text-left bg-violet-50 border border-violet-200 rounded p-4 mb-4">
                  <p className="text-sm text-violet-800 mb-2">
                    <strong>🎉 Your Subscriber Benefits:</strong>
                  </p>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-violet-700">Regular Price:</span>
                    <span className="text-sm text-violet-700 line-through">$15.00</span>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-violet-700">Your Discount:</span>
                    <span className="text-sm font-semibold text-violet-700">50% OFF</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-violet-300">
                    <span className="text-base font-bold text-violet-900">Your Price:</span>
                    <span className="text-base font-bold text-violet-900">$7.50</span>
                  </div>
                </div>
              ) : (user.role === "admin" || user.role === "beta_tester") ? (
                // Admin/Beta Tester - Free
                <div className="text-left bg-purple-50 border border-purple-200 rounded p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-bold text-purple-900">Service Fee:</span>
                    <span className="text-base font-bold text-purple-900">FREE</span>
                  </div>
                  <p className="text-xs text-purple-700 mt-2">Unlimited Free Access - Included with your account</p>
                </div>
              ) : (
                // No subscription (free_user or a_la_carte) - Full price
                <div className="text-left bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-bold text-blue-900">Service Fee:</span>
                    <span className="text-base font-bold text-blue-900">$15.00</span>
                  </div>
                  <p className="text-xs text-blue-700 mt-2">Standard Service - Full Price</p>
                </div>
              )}
              
              <div className="text-left bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                <p className="text-xs text-yellow-800">
                  <strong>⚠️ Important:</strong> Analysis charges are non-refundable. Please verify your manuscript details before proceeding.
                </p>
              </div>
              
              <div className="text-left bg-slate-50 border border-slate-200 rounded p-3 mb-4">
                <p className="text-sm text-slate-800"><strong>Title:</strong> {formData.title}</p>
                <p className="text-sm text-slate-800"><strong>Author:</strong> {formData.author_name}</p>
                <p className="text-sm text-slate-800"><strong>Genre:</strong> {genres.find(g => g.value === formData.genre)?.label}</p>
                {formData.sub_genre && (
                  <p className="text-sm text-slate-800"><strong>Sub-Genre:</strong> {formData.sub_genre}</p>
                )}
                <p className="text-sm text-slate-800"><strong>File:</strong> {selectedFile?.name}</p>
              </div>
              
              <p className="text-sm text-slate-600 mb-6">
                Ready to analyze your manuscript? Click confirm to begin analysis.
              </p>
              
              <div className="flex space-x-3">
                <Button 
                  onClick={handleConfirmAnalysis}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {user.role === "subscriber" ? "Confirm - Charge $5.00" : 
                   user.role === "a_la_carte" ? "Confirm - Charge $10.00" : 
                   "Confirm - Start Analysis"}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleCancelAnalysis}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && reportToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="text-center">
              <div className="text-4xl mb-4">🗑️</div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Delete Report?
              </h3>
              
              <div className="text-left bg-red-50 border border-red-200 rounded p-4 mb-4">
                <p className="text-sm text-red-800 mb-2">
                  <strong>Warning:</strong> This action cannot be undone.
                </p>
                <p className="text-sm text-red-700">
                  You are about to permanently delete the report for:
                </p>
                <p className="text-sm font-semibold text-red-900 mt-2">
                  "{reportToDelete.title}"
                </p>
              </div>
              
              <p className="text-sm text-slate-600 mb-6">
                Are you absolutely sure you want to delete this PDE report? 
                This will remove all analysis data and cannot be recovered.
              </p>
              
              <div className="flex space-x-3">
                <Button 
                  onClick={confirmDeleteReport}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  Yes, Delete Report
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowDeleteModal(false);
                    setReportToDelete(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                alt="Digital Ink AI" 
                className="h-8 w-auto"
              />
            </div>
            <p className="text-slate-400">
              © 2025 Use What You Got Publishing, LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      
      {/* Payment Validation Modal */}
      <PaymentValidationModal
        isOpen={showPaymentValidation}
        onClose={() => setShowPaymentValidation(false)}
        onSuccess={handlePaymentValidationSuccess}
        redirectLocation="pde"
      />
      
      {/* Profile Upgrade Modal */}
      <ProfileUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleProfileUpgrade}
        serviceRequested="PDE Analysis"
      />
      
      {/* Service Charge Confirmation Modal */}
      <ServiceChargeModal
        isOpen={showChargeModal}
        onClose={() => setShowChargeModal(false)}
        onConfirm={handleServiceChargeAuthorization}
        serviceInfo={chargeInfo}
        userType={user?.role || 'a_la_carte'}
      />

      {/* Processing Modal */}
      <ProcessingModal 
        isOpen={isProcessing} 
        serviceName="Professional Developmental Editor (PDE)"
        bookTitle={formData.title}
        progress={processingProgress}
        currentStep={currentStep}
        progressSteps={progressSteps}
      />

      {/* API Retry Modal */}
      <APIRetryModal
        isOpen={showRetryModal}
        attemptNumber={retryAttempt}
        maxAttempts={maxRetryAttempts}
        isRetrying={isRetrying}
        errorMessage={retryError}
        onManualRetry={handleManualRetry}
        onCancel={handleCancelRetry}
        serviceName="Professional Developmental Editor (PDE)"
      />

      {/* Email Receipt Modal */}
      <EmailReceiptModal
        isOpen={showEmailReceiptModal}
        onClose={handleCloseEmailReceiptModal}
        onSendReceipt={handleSendReceipt}
        serviceName="Professional Developmental Editor (PDE)"
        amount={lastCharge?.amount || 10.00}
        bookTitle={lastCharge?.bookTitle || "PDE Analysis"}
      />
    </div>
  );
};

export default PDEPage;